import React, { Component } from 'react';
import { NavigationButton } from 'web-ui-components/lib/navigation/buttons';
import { StandardBody } from 'web-ui-components/lib/typography/body';
import { PageHeading } from 'web-ui-components/lib/typography/headings';
import { PhoneLink } from 'web-ui-components/lib/actions/links';
import { PHONE_NUMBER } from 'constants/general';
import { TimedOutErrorContainer } from '../TimedOutError/TimedOutErrorContainer';

class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  shouldComponentUpdate() {
    return true;
  }

  componentDidUpdate(prevProps) {
    /**
     * `GlobalErrorBoundary` can be manually forced to display the error screen via props.
     * Just for clarity, we don't ever want to be able to "unset" `isError` from `true` once it is set.
     */
    if (prevProps.isError !== this.props.isError && this.props.isError === true) {
      this.setState({ hasError: true });
    }
  }

  /**
   * @param {Error} error The error that was thrown. In practice, it will usually be an instance of {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error | Error} but this is not guaranteed because JavaScript
   * allows to {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/throw | throw} any value, including strings or even `null`.
   * @param {*} info An object containing additional information about the error.
   * Its componentStack field contains a stack trace with the component that threw, as well as the names and source locations of all its parent components.
   * In production, the component names will be minified. If you set up production error reporting, you can decode the component stack using sourcemaps the same way as you would do for regular JavaScript error stacks.
   * @see {@link https://react.dev/reference/react/Component#componentdidcatch}
   */
  componentDidCatch(error, info) {
    try {
      if (window.dtrum) {
        /**
         * @type {import('@dynatrace/dtrum-api-types').DtrumApi}
         */
        window.dtrum.reportError(error);
      } else {
        try {
          console.dir(info);
          console.log(error);
        } catch (e) {}
      }
    } catch (e) {
      /**
       * For some reason, we hit an error trying to use `dtrum`!
       * This could for a few reasons:
       * 1. Dynatrace has changed their `dtrum` API, and we tried to call something that has been renamed/removed or refactored.
       * 2. The Dynatrace object has had it's shape altered/wiped altogether, but not deleted from `window`. This might have been done in error
       * by a third party script, or deliberately to block/sabotage execution.
       */
      try {
        console.log('dtrum error');
        console.dir(info);
        console.log(error);
      } catch (e) {
        // we can't even console log for some reason. Potentially a weird user agent.
      }
    }
  }

  render() {
    const {
      onReturnHome,
      phoneNumber = PHONE_NUMBER,
      children,
      isNative,
      hasUserTimedOut
    } = this.props;

    const clearError = () => this.setState({ hasError: false });

    return this.state.hasError ? (
      hasUserTimedOut ? (
        <TimedOutErrorContainer clearError={clearError} />
      ) : (
        <>
          <PageHeading>{`Hmm, something’s gone wrong`}</PageHeading>
          <StandardBody>
            <p>{`Looks like there may have been a technical problem. Can you give it another go later?`}</p>
            {!isNative ? (
              <p>{`Still not working? Give us a call on ${phoneNumber} and we’ll get to the bottom of it. We’re here Monday to Friday, 8am to 6pm and Saturday, 9am to 1pm.`}</p>
            ) : (
              <p>
                {`Still not working? Give us a call on `}
                <PhoneLink phoneNumber={PHONE_NUMBER} />
                {` and we’ll get to the bottom of it. We’re here Monday to Friday, 8am to 6pm and Saturday, 9am to 1pm.`}
              </p>
            )}
          </StandardBody>
          <div className="c-m-button-group">
            <NavigationButton.Tertiary labelText={`Return to home screen`} onClick={onReturnHome} />
          </div>
        </>
      )
    ) : (
      children
    );
  }
}

export { GlobalErrorBoundary };
